import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  AcademicCapIcon,
  SparklesIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const AytJourneyPage = () => {
  const [journey, setJourney] = useState(null);
  const [topicProgress, setTopicProgress] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [weekInfo, setWeekInfo] = useState(null);
  const [weeklyPlans, setWeeklyPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [activeTab, setActiveTab] = useState('this-week'); // this-week, all-topics, timeline
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [programType, setProgramType] = useState(''); // 'smart' or 'free'
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]); // Bugün
  const [targetDate, setTargetDate] = useState('2026-06-21');
  const [weeklyHours, setWeeklyHours] = useState(20);
  const [startingTopicId, setStartingTopicId] = useState(null);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changingTopicIndex, setChangingTopicIndex] = useState(null);
  const [availableTopics, setAvailableTopics] = useState([]);
  const [lockedTopics, setLockedTopics] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [changingTopic, setChangingTopic] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchJourney();
  }, []);

  const fetchJourney = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Try to get existing journey
      try {
        const response = await axios.get(`${API_URL}/ayt-journey/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          setJourney(response.data.data.journey);
          setTopicProgress(response.data.data.topicProgress);
          
          // Check if journey has targetExamDate - if not, show onboarding
          if (!response.data.data.journey.targetExamDate) {
            setShowOnboarding(true);
          } else {
            // Fetch recommendations and weekly plans
            fetchRecommendations();
            fetchWeeklyPlans();
          }
        }
      } catch (err) {
        if (err.response?.status === 404) {
          // No journey found, create one and show onboarding
          const createResponse = await axios.post(
            `${API_URL}/ayt-journey/create`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (createResponse.data.success) {
            setJourney(createResponse.data.data.journey);
            setTopicProgress(createResponse.data.data.topicProgress);
            setShowOnboarding(true);
          }
        } else {
          throw err;
        }
      }
    } catch (error) {
      console.error('Journey yükleme hatası:', error);
      alert('Yolculuk yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      setRecommendationsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ayt-journey/recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setRecommendations(response.data.data.recommendations);
        if (response.data.data.weekInfo) {
          setWeekInfo(response.data.data.weekInfo);
        }
      }
    } catch (error) {
      console.error('Öneriler yüklenirken hata:', error);
    } finally {
      setRecommendationsLoading(false);
    }
  };

  // Fetch weekly plans from database
  const fetchWeeklyPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ayt-journey/weekly-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setWeeklyPlans(response.data.data.weeklyPlans);
      }
    } catch (error) {
      console.error('Haftalık planlar yüklenirken hata:', error);
    }
  };

  const handleGenerateProgram = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ayt-journey/generate-program`,
        {
          startDate: startDate,
          targetExamDate: targetDate,
          weeklyStudyHours: weeklyHours
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setJourney(response.data.data.journey);
        setWeeklyPlans(response.data.data.weeklyPlans);
        setShowOnboarding(false);
        setActiveTab('timeline');
        // Fetch recommendations after program is created
        await fetchRecommendations();
        alert(`${response.data.data.weeklyPlans.length} haftalık akıllı programınız hazır!`);
      }
    } catch (error) {
      console.error('Program oluşturma hatası:', error);
      alert('Program oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipOnboarding = () => {
    setShowOnboarding(false);
    setProgramType('free');
  };

  const handleResetJourney = async () => {
    if (!window.confirm('Tüm ilerlemeniz sıfırlanacak. Emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ayt-journey/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Program sıfırlandı!');
        window.location.reload();
      }
    } catch (error) {
      console.error('Reset hatası:', error);
      alert('Sıfırlama sırasında bir hata oluştu');
    }
  };

  const fetchAvailableTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ayt-journey/available-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAvailableTopics(response.data.data.availableTopics);
        setLockedTopics(response.data.data.lockedTopics);
      }
    } catch (error) {
      console.error('Secilir konular yuklenirken hata:', error);
    }
  };

  const handleOpenChangeModal = async (index) => {
    setChangingTopicIndex(index);
    setShowChangeModal(true);
    setModalLoading(true);
    try {
      await fetchAvailableTopics();
    } finally {
      setModalLoading(false);
    }
  };

  const handleSelectNewTopic = async (newTopic) => {
    if (changingTopicIndex === null || !weekInfo) return;

    setChangingTopic(true);
    try {
      const token = localStorage.getItem('token');
      const oldTopic = recommendations[changingTopicIndex];

      const response = await axios.put(
        `${API_URL}/ayt-journey/weekly-plan/${weekInfo.weekNumber}`,
        {
          topics: [
            { topicId: oldTopic.topicId, action: 'remove' },
            { topicId: newTopic.topicId, action: 'add' }
          ]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Show warnings if any, then reload
        if (response.data.data.warnings && response.data.data.warnings.length > 0) {
          const warningMessages = response.data.data.warnings.map(w => w.message).join('\n');
          alert(`Plan guncellendi!\n\nUyarilar:\n${warningMessages}`);
        }
        // Reload page to refresh everything
        window.location.reload();
      }
    } catch (error) {
      console.error('Konu degistirme hatasi:', error);
      alert('Konu degistirilirken bir hata olustu');
      setChangingTopic(false);
    }
  };

  const handleStartTopic = async (topicProgressId, topicName) => {
    try {
      setStartingTopicId(topicProgressId);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/ayt-journey/progress/${topicProgressId}`,
        { status: 'in_progress' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update local state
        setTopicProgress(prev =>
          prev.map(tp =>
            tp.id === topicProgressId ? { ...tp, status: 'in_progress' } : tp
          )
        );

        // Update recommendations status (don't fetch new ones!)
        setRecommendations(prev =>
          prev.map(rec =>
            rec.topicProgressId === topicProgressId
              ? { ...rec, status: 'in_progress' }
              : rec
          )
        );

        // Success feedback
        setTimeout(() => {
          alert(`✅ "${topicName}" konusuna başladın! Devam Eden Konular'da görünüyor.`);
        }, 300);
      }
    } catch (error) {
      console.error('Konu başlatma hatası:', error);
      alert('Bir hata oluştu');
    } finally {
      setStartingTopicId(null);
    }
  };

  const toggleTopicCompletion = async (topicProgressId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ayt-journey/progress/${topicProgressId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const newStatus = response.data.data.topicProgress.status;

        // Update local state
        setTopicProgress(prev =>
          prev.map(tp =>
            tp.id === topicProgressId ? response.data.data.topicProgress : tp
          )
        );

        // Update recommendations status (don't fetch new ones!)
        setRecommendations(prev =>
          prev.map(rec =>
            rec.topicProgressId === topicProgressId
              ? { ...rec, status: newStatus }
              : rec
          )
        );

        // Update journey stats
        setJourney(prev => ({
          ...prev,
          completedTopicsCount: response.data.data.journey.completedTopicsCount
        }));
      }
    } catch (error) {
      console.error('Konu tamamlama hatası:', error);
      alert('Bir hata oluştu');
    }
  };

  // Group topics by subject and sort by order
  const groupedTopics = topicProgress.reduce((acc, tp) => {
    const subject = tp.topic.subject;
    if (!acc[subject]) {
      acc[subject] = [];
    }
    acc[subject].push(tp);
    return acc;
  }, {});

  // Sort each subject's topics by order
  Object.keys(groupedTopics).forEach(subject => {
    groupedTopics[subject].sort((a, b) => (a.topic.order || 0) - (b.topic.order || 0));
  });

  const subjects = ['Matematik', 'Fizik', 'Kimya', 'Biyoloji'];
  
  // Filter topics based on selected subject
  const filteredTopics = selectedSubject === 'all' 
    ? topicProgress 
    : topicProgress.filter(tp => tp.topic.subject === selectedSubject);

  // Calculate statistics
  const completedCount = topicProgress.filter(tp => tp.status === 'completed').length;
  const inProgressCount = topicProgress.filter(tp => tp.status === 'in_progress').length;
  const totalCount = topicProgress.length;
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Subject colors
  const subjectColors = {
    'Matematik': 'bg-blue-500',
    'Fizik': 'bg-purple-500',
    'Kimya': 'bg-green-500',
    'Biyoloji': 'bg-pink-500'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yolculuğunuz yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 animate-fade-in relative">
            {/* Close Button */}
            <button
              onClick={() => setShowOnboarding(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {onboardingStep === 1 && (
              <div className="text-center">
                <div className="h-20 w-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SparklesIcon className="h-12 w-12 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">AYT Yolculuğuna Hoş Geldin! 🎯</h2>
                <p className="text-gray-600 mb-8 text-lg">
                  103 AYT konusunu en verimli şekilde nasıl çalışmak istersin?
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Smart Program */}
                  <button
                    onClick={() => {
                      setProgramType('smart');
                      setOnboardingStep(2);
                    }}
                    className="p-6 border-2 border-blue-500 rounded-xl hover:bg-blue-50 transition text-left group"
                  >
                    <div className="text-4xl mb-3">🤖</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Akıllı Program</h3>
                    <p className="text-gray-600 text-sm">
                      AI ile 28 haftalık kişiselleştirilmiş çalışma programı oluştur
                    </p>
                    <div className="mt-4 text-blue-600 font-medium group-hover:translate-x-2 transition-transform inline-flex items-center">
                      Devam Et →
                    </div>
                  </button>

                  {/* Free Study */}
                  <button
                    onClick={handleSkipOnboarding}
                    className="p-6 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition text-left group"
                  >
                    <div className="text-4xl mb-3">📚</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Serbest Çalış</h3>
                    <p className="text-gray-600 text-sm">
                      Konuları kendin seç, istediğin gibi çalış ve takip et
                    </p>
                    <div className="mt-4 text-gray-600 font-medium group-hover:translate-x-2 transition-transform inline-flex items-center">
                      Devam Et →
                    </div>
                  </button>
                </div>
              </div>
            )}

            {onboardingStep === 2 && programType === 'smart' && (
              <div>
                <button
                  onClick={() => setOnboardingStep(1)}
                  className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Geri
                </button>

                <h2 className="text-2xl font-bold text-gray-900 mb-6">Program Ayarları</h2>

                <div className="space-y-6">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 Başlangıç Tarihi
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Programınız bu tarihten itibaren başlayacak
                    </p>
                  </div>

                  {/* Target Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🎯 Hedef Sınav Tarihi
                    </label>
                    <input
                      type="date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      YKS genelde Haziran'ın 2-3. haftasında yapılır
                    </p>
                  </div>

                  {/* Weekly Hours */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ⏰ Haftalık Çalışma Saati: <span className="text-blue-600 font-bold">{weeklyHours} saat</span>
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="50"
                      value={weeklyHours}
                      onChange={(e) => setWeeklyHours(Number(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>5 saat</span>
                      <span>25 saat (önerilen)</span>
                      <span>50 saat</span>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Tahmini:</strong> {Math.ceil((new Date(targetDate) - new Date()) / (1000 * 60 * 60 * 24 * 7))} hafta içinde,
                      haftada {weeklyHours} saat çalışarak tüm konuları bitirebilirsin!
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex gap-4">
                  <button
                    onClick={handleSkipOnboarding}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition"
                  >
                    Şimdilik Atlat
                  </button>
                  <button
                    onClick={handleGenerateProgram}
                    disabled={loading}
                    className={`flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition ${
                      loading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Program Oluşturuluyor...</span>
                      </span>
                    ) : (
                      '🚀 Program Oluştur'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Change Topic Modal */}
      {showChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Konu Degistir</h2>
                <button
                  onClick={() => {
                    setShowChangeModal(false);
                    setChangingTopicIndex(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Filters */}
              <div className="mt-4 flex flex-wrap gap-2">
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex-1 min-w-[150px]"
                />
                <select
                  value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="all">Tum Dersler</option>
                  <option value="Matematik">Matematik</option>
                  <option value="Fizik">Fizik</option>
                  <option value="Kimya">Kimya</option>
                  <option value="Biyoloji">Biyoloji</option>
                </select>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[50vh]">
              {/* Loading State */}
              {modalLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Konular yukleniyor...</p>
                </div>
              ) : changingTopic ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Konu degistiriliyor...</p>
                </div>
              ) : (
                <>
                  {/* Available Topics */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Secilebilir Konular</h3>
                    <div className="space-y-2">
                      {availableTopics
                        .filter(t => subjectFilter === 'all' || t.subject === subjectFilter)
                        .filter(t => searchQuery === '' || t.name.toLowerCase().includes(searchQuery.toLowerCase()))
                        .filter(t => !recommendations.some(r => r.topicId === t.topicId))
                        .map(topic => (
                          <button
                            key={topic.topicProgressId}
                            onClick={() => handleSelectNewTopic(topic)}
                            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-blue-50 rounded-lg border border-gray-200 hover:border-blue-300 transition text-left"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{topic.name}</p>
                              <p className="text-sm text-gray-500">
                                {topic.subject} | {topic.estimatedHours} saat
                                {topic.progress > 0 && ` | %${topic.progress}`}
                              </p>
                            </div>
                            <span className="text-blue-600 text-sm font-medium">Sec →</span>
                          </button>
                        ))}
                      {availableTopics
                        .filter(t => subjectFilter === 'all' || t.subject === subjectFilter)
                        .filter(t => searchQuery === '' || t.name.toLowerCase().includes(searchQuery.toLowerCase()))
                        .filter(t => !recommendations.some(r => r.topicId === t.topicId))
                        .length === 0 && (
                        <p className="text-center text-gray-500 py-4">Secilebilir konu bulunamadi</p>
                      )}
                    </div>
                  </div>

                  {/* Locked Topics */}
                  {lockedTopics.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Kilitli Konular</h3>
                      <div className="space-y-2">
                        {lockedTopics
                          .filter(t => subjectFilter === 'all' || t.subject === subjectFilter)
                          .filter(t => searchQuery === '' || t.name.toLowerCase().includes(searchQuery.toLowerCase()))
                          .slice(0, 5)
                          .map(topic => (
                            <div
                              key={topic.topicProgressId}
                              className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border border-gray-200 opacity-60"
                            >
                              <div>
                                <p className="font-medium text-gray-700">{topic.name}</p>
                                <p className="text-sm text-red-500">{topic.lockedReason}</p>
                              </div>
                              <span className="text-gray-400">🔒</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowChangeModal(false);
                  setChangingTopicIndex(null);
                }}
                className="w-full py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Iptal
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Dashboard'a Dön
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <SparklesIcon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">AYT Yolculuğum</h1>
                  <p className="text-gray-600">Konuları takip et, hedefine ulaş</p>
                </div>
              </div>
              {journey?.targetExamDate && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowOnboarding(true)}
                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                  >
                    ⚙️ Ayarlar
                  </button>
                  <button
                    onClick={handleResetJourney}
                    className="px-4 py-2 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition"
                  >
                    🔄 Sıfırla
                  </button>
                </div>
              )}
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-600 mb-1">
                  <AcademicCapIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Toplam Konu</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">{totalCount}</p>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-green-600 mb-1">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Tamamlanan</span>
                </div>
                <p className="text-2xl font-bold text-green-900">{completedCount}</p>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-purple-600 mb-1">
                  <ClockIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Devam Eden</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">{inProgressCount}</p>
              </div>

              <div className="bg-pink-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-pink-600 mb-1">
                  <CalendarIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">İlerleme</span>
                </div>
                <p className="text-2xl font-bold text-pink-900">%{completionPercentage}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Genel İlerleme</span>
                <span className="text-sm font-semibold text-gray-900">
                  {completedCount} / {totalCount}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 bg-white rounded-xl shadow-sm p-2 flex gap-2">
          <button
            onClick={() => setActiveTab('this-week')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition ${
              activeTab === 'this-week'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            🎯 Bu Hafta
          </button>
          <button
            onClick={() => setActiveTab('all-topics')}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition ${
              activeTab === 'all-topics'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            📚 Tüm Konular
          </button>
          {journey?.targetExamDate && (
            <button
              onClick={() => setActiveTab('timeline')}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition ${
                activeTab === 'timeline'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              📅 Timeline
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'this-week' && (
          <div className="space-y-6">
            {/* AI Recommendations */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <SparklesIcon className="h-6 w-6 text-yellow-500 mr-2" />
                  Bu Hafta Ne Çalışmalıyım?
                </h2>
                {weekInfo && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500">
                      📅 {new Date(weekInfo.startDate + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - {new Date(weekInfo.endDate + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                    </span>
                    {recommendations.length > 0 && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        ⏱️ {recommendations.reduce((sum, r) => sum + (r.estimatedHours || 0), 0)} saat
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {recommendationsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Çalışma programı yükleniyor...</p>
                </div>
              ) : recommendations.length > 0 ? (
                <div className="space-y-3">
                  {recommendations.map((rec, index) => (
                    <div
                      key={rec.topicProgressId}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 hover:shadow-md transition"
                    >
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl font-bold text-blue-600">#{index + 1}</span>
                        <div>
                          <h3 className="font-bold text-gray-900">{rec.topic.name}</h3>
                          <p className="text-sm text-gray-600">
                            {rec.topic.subject}
                            {rec.estimatedHours && (
                              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                ⏱️ {rec.estimatedHours} saat
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-blue-600 mt-1">💡 {rec.reason}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOpenChangeModal(index)}
                          className="px-3 py-2 rounded-lg font-medium bg-orange-100 hover:bg-orange-200 text-orange-700 transition text-sm"
                        >
                          Degistir
                        </button>
                        <button
                          onClick={() => navigate(`/ayt-journey/topic/${rec.topicProgressId}`)}
                          className="px-3 py-2 rounded-lg font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition text-sm"
                        >
                          Detay
                        </button>
                        <button
                          onClick={() => handleStartTopic(rec.topicProgressId, rec.topic.name)}
                          disabled={startingTopicId === rec.topicProgressId || rec.status !== 'not_started'}
                          className={`px-4 py-2 rounded-lg font-medium transition ${
                            startingTopicId === rec.topicProgressId
                              ? 'bg-gray-400 cursor-not-allowed text-white'
                              : rec.status === 'completed'
                              ? 'bg-green-100 text-green-700 cursor-default'
                              : rec.status === 'in_progress'
                              ? 'bg-yellow-100 text-yellow-700 cursor-default'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {startingTopicId === rec.topicProgressId ? (
                            <span className="flex items-center space-x-2">
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Baslatiliyor...</span>
                            </span>
                          ) : rec.status === 'completed' ? (
                            '✓ Bitti'
                          ) : rec.status === 'in_progress' ? (
                            '⏳ Calisiyorum'
                          ) : (
                            'Basla →'
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Henüz öneri bulunmuyor</p>
                  <p className="text-sm mt-2">Çalışma programınız oluşturulduğunda burada görünecek</p>
                </div>
              )}
            </div>

            {/* In Progress Topics */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">⏳ Devam Eden Konular</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {topicProgress.filter(tp => tp.status === 'in_progress').slice(0, 6).map(tp => (
                  <TopicCard
                    key={tp.id}
                    topicProgress={tp}
                    onToggle={toggleTopicCompletion}
                    subjectColor={subjectColors[tp.topic.subject]}
                    onNavigate={(id) => navigate(`/ayt-journey/topic/${id}`)}
                  />
                ))}
              </div>
              {topicProgress.filter(tp => tp.status === 'in_progress').length === 0 && (
                <p className="text-gray-500 text-center py-4">Henüz devam eden konu yok</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'all-topics' && (
          <>
            {/* Subject Filter */}
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={() => setSelectedSubject('all')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              selectedSubject === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Tümü ({totalCount})
          </button>
          {subjects.map(subject => {
            const count = groupedTopics[subject]?.length || 0;
            const completed = groupedTopics[subject]?.filter(tp => tp.status === 'completed').length || 0;
            return (
              <button
                key={subject}
                onClick={() => setSelectedSubject(subject)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  selectedSubject === subject
                    ? `${subjectColors[subject]} text-white`
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {subject} ({completed}/{count})
              </button>
            );
          })}
        </div>

        {/* Topics Grid - Grouped by Subject */}
        {selectedSubject === 'all' ? (
          // Show all subjects in sections
          subjects.map(subject => {
            const subjectTopics = groupedTopics[subject] || [];
            if (subjectTopics.length === 0) return null;

            return (
              <div key={subject} className="mb-8">
                <h2 className={`text-xl font-bold mb-4 flex items-center ${
                  subject === 'Matematik' ? 'text-blue-700' :
                  subject === 'Fizik' ? 'text-purple-700' :
                  subject === 'Kimya' ? 'text-green-700' :
                  'text-pink-700'
                }`}>
                  <span className={`h-3 w-3 rounded-full mr-3 ${subjectColors[subject]}`}></span>
                  {subject}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {subjectTopics.map(tp => (
                    <TopicCard
                      key={tp.id}
                      topicProgress={tp}
                      onToggle={toggleTopicCompletion}
                      subjectColor={subjectColors[subject]}
                      onNavigate={(id) => navigate(`/ayt-journey/topic/${id}`)}
                    />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          // Show single subject
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTopics.map(tp => (
              <TopicCard
                key={tp.id}
                topicProgress={tp}
                onToggle={toggleTopicCompletion}
                subjectColor={subjectColors[tp.topic.subject]}
                onNavigate={(id) => navigate(`/ayt-journey/topic/${id}`)}
              />
            ))}
          </div>
        )}
          </>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">📅 28 Haftalık Roadmap</h2>
            
            {weeklyPlans.length > 0 ? (
              <div className="space-y-4">
                {weeklyPlans.slice(0, 8).map((plan) => (
                  <div
                    key={plan.id}
                    className={`p-4 rounded-lg border-2 ${
                      plan.status === 'active' ? 'border-blue-500 bg-blue-50' :
                      plan.status === 'completed' ? 'border-green-500 bg-green-50' :
                      'border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">
                          {plan.status === 'active' ? '⏳' :
                           plan.status === 'completed' ? '✅' : '⭕'}
                        </span>
                        <div>
                          <h3 className="font-bold text-gray-900">Hafta {plan.weekNumber}</h3>
                          <p className="text-sm text-gray-600">
                            {new Date(plan.startDate + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - {new Date(plan.endDate + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-600">
                        {plan.completedQuestions} / {plan.targetQuestions} soru
                      </span>
                    </div>
                    
                    {plan.topics && plan.topics.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {plan.topics.map((topic, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-white rounded-full text-xs font-medium text-gray-700 border"
                          >
                            {topic.topicName} ({topic.estimatedHours}sa)
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                {weeklyPlans.length > 8 && (
                  <p className="text-center text-gray-500 text-sm">
                    ... ve {weeklyPlans.length - 8} hafta daha
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">Henüz haftalık program oluşturulmadı</p>
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
                >
                  🚀 Program Oluştur
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Topic Card Component
const TopicCard = ({ topicProgress, onToggle, subjectColor, onNavigate }) => {
  const isCompleted = topicProgress.status === 'completed';
  const isInProgress = topicProgress.status === 'in_progress';
  const progress = topicProgress.progress || 0;
  const estimatedHours = topicProgress.topic?.estimatedHours || 3;
  const totalMinutes = topicProgress.totalStudyMinutes || 0;

  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}dk`;
    if (m === 0) return `${h}sa`;
    return `${h}sa ${m}dk`;
  };

  return (
    <div
      onClick={() => onNavigate && onNavigate(topicProgress.id)}
      className={`bg-white rounded-lg shadow hover:shadow-lg transition-all duration-200 p-4 border-2 cursor-pointer ${
        isCompleted ? 'border-green-400 bg-green-50' :
        isInProgress ? 'border-purple-300' :
        'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-1">
            {topicProgress.topic.name}
          </h3>
          <div className="flex items-center space-x-2">
            <span className={`inline-block px-2 py-0.5 ${subjectColor} text-white text-xs rounded-full`}>
              {topicProgress.topic.subject}
            </span>
            <span className="text-xs text-gray-500">{estimatedHours}sa</span>
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle(topicProgress.id);
          }}
          className="ml-2 flex-shrink-0"
        >
          {isCompleted ? (
            <CheckCircleSolid className="h-6 w-6 text-green-500" />
          ) : (
            <div className="h-6 w-6 rounded-full border-2 border-gray-300 hover:border-blue-500 transition"></div>
          )}
        </button>
      </div>

      {/* Progress Bar - Always shown */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-500">{progress}%</span>
          {totalMinutes > 0 && (
            <span className="text-gray-400">{formatDuration(totalMinutes)}</span>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`${subjectColor} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center justify-between text-xs">
        <span className={`font-medium ${
          isCompleted ? 'text-green-600' :
          isInProgress ? 'text-purple-600' :
          'text-gray-500'
        }`}>
          {isCompleted ? '✓ Tamamlandi' :
           isInProgress ? '⏳ Devam ediyor' :
           '○ Baslanmadi'}
        </span>
      </div>
    </div>
  );
};

export default AytJourneyPage;
